import { ApiError } from "@/lib/api-types";
import type { HttpClient } from "@/lib/http";
import { http } from "@/lib/http";

import type {
  DeviceBucketData,
  DeviceLoadStats,
  DeviceNotificationsPage,
  DeviceSettings,
  DeviceSuccessRates,
  Device,
  DevicesListResult,
  LaunchAutomationInput,
  LaunchAutomationResult,
  LoadStatsPeriod,
  MarketPublishInput,
  MarketSearchParams,
  MarketSearchResult,
  ProxyConnection,
  RentDeviceInput,
  ShareDevicesInput,
  SuccessRatesParams,
  TriggerLogsResult,
} from "@/features/devices/types";

import type {
  AddDeviceResponseDto,
  DeviceBucketResponseDto,
  DeviceNotificationsResponseDto,
  DeviceSettingsDto,
  GetDevicesResponseDto,
  LaunchAutomationResponseDto,
  LoadStatsResponseDto,
  MarketResponseDto,
  ProxyBandwidthResponseDto,
  ProxyBlacklistResponseDto,
  ProxyConnectionResponseDto,
  ProxyConnectionsResponseDto,
  ProxyRotateResponseDto,
  ReadNotificationsResponseDto,
  SuccessRatesResponseDto,
  TriggerLogsResponseDto,
} from "./dto";
import {
  aggregateBandwidth,
  mapDeviceBucket,
  mapDeviceNotifications,
  mapDeviceSettings,
  mapDevicesResponse,
  mapLaunchAutomation,
  mapLoadStats,
  mapMarket,
  mapProxyConnection,
  mapSuccessRates,
  mapTriggerLogs,
} from "./mappers";

/**
 * Typed data access for the Devices slice. The repository is the seam between
 * the app and the backend: it speaks DTOs on the wire and returns domain types.
 * Everything above (hooks, components) is transport-agnostic.
 */
export interface DevicesRepository {
  listDevices(page: number, pageSize?: number): Promise<DevicesListResult>;
  getSuccessRates(params: SuccessRatesParams): Promise<DeviceSuccessRates>;
  getLoadStats(period: LoadStatsPeriod): Promise<DeviceLoadStats>;
  deleteDevice(deviceId: string): Promise<void>;
  publishToMarket(input: MarketPublishInput): Promise<void>;
  unpublishFromMarket(deviceIds: string[]): Promise<void>;
  addDevice(hashcode: string): Promise<void>;
  /**
   * Share owned devices with another user as a free daily `live` rental.
   * Needs the RECIPIENT's username — the endpoint rejects a body without one.
   */
  shareDevices(input: ShareDevicesInput): Promise<void>;

  searchMarket(params: MarketSearchParams): Promise<MarketSearchResult>;
  rentDevice(input: RentDeviceInput): Promise<void>;

  getSettings(deviceId: string): Promise<DeviceSettings>;
  generateTriggerLink(deviceId: string, label: string): Promise<void>;
  deleteTriggerLink(deviceId: string, triggerLinkId: string): Promise<void>;
  setMinInterval(deviceId: string, interval: number): Promise<void>;
  setAutoTrigger(deviceId: string, interval: number): Promise<void>;
  getTriggerLogs(
    deviceId: string,
    skip: number,
    limit: number,
  ): Promise<TriggerLogsResult>;

  // Buckets
  getBucket(remoteDeviceId: string): Promise<DeviceBucketData>;
  saveBucket(
    remoteDeviceId: string,
    data: DeviceBucketData,
  ): Promise<DeviceBucketData>;
  deleteBucket(remoteDeviceId: string): Promise<void>;

  // Proxy
  listProxyConnections(): Promise<ProxyConnection[]>;
  createProxyConnection(
    deviceId: string,
    name?: string,
  ): Promise<ProxyConnection>;
  deleteProxyConnection(connectionId: string): Promise<void>;
  setProxyAssociation(connectionId: string, deviceId: string): Promise<void>;
  rotateProxyPassword(connectionId: string): Promise<string>;
  listProxyBlacklist(connectionId: string): Promise<string[]>;
  addProxyBlacklist(connectionId: string, domains: string[]): Promise<void>;
  removeProxyBlacklist(connectionId: string, domains: string[]): Promise<void>;

  // Notifications
  listDeviceNotifications(
    page: number,
    limit: number,
  ): Promise<DeviceNotificationsPage>;
  markNotificationsRead(notificationIds: string[]): Promise<number>;

  // Automations
  launchAutomation(input: LaunchAutomationInput): Promise<LaunchAutomationResult>;
}

/** An empty fleet is a valid state — but the backend signals it with a non-2xx
 * status (`404 {success:false, devices:[], message:"No devices to display"}`)
 * rather than `200 {devices:[]}`, so the HTTP client throws it as an `ApiError`.
 * This recognizes that specific envelope so `listDevices` can return an empty
 * result (→ empty-state UI) instead of the error surface. A genuine 404 (wrong
 * route, unknown resource) has no `devices`/`message` envelope and still throws.
 *
 * Only the `page:1` path is guarded here: the fleet always requests page 1, and
 * `page:0` on an empty fleet is a separate backend bug that 500s (not 404), so
 * it's out of scope for this "no devices yet" case. */
function isEmptyFleetError(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 404) return false;
  const body = err.body;
  if (typeof body !== "object" || body === null) return false;
  const record = body as Record<string, unknown>;
  return (
    Array.isArray(record.devices) ||
    (typeof record.message === "string" && /no devices/i.test(record.message))
  );
}

const EMPTY_DEVICES_LIST: DevicesListResult = {
  devices: [],
  ppaDevicePresent: false,
  totalDevices: 0,
};

export function createDevicesRepository(client: HttpClient): DevicesRepository {
  return {
    async listDevices(page, pageSize) {
      // Legacy sends `limit` only when a page size is chosen; `0` = "All".
      const body: Record<string, unknown> = {
        page,
        includeIterations: "true",
      };
      if (pageSize !== undefined) body.limit = pageSize === 0 ? 99999 : pageSize;
      try {
        const dto = await client.post<GetDevicesResponseDto>(
          "/api/v1/device/get_devices",
          body,
        );
        return mapDevicesResponse(dto);
      } catch (err) {
        // A device-less account is an empty fleet, not an error (see helper).
        if (isEmptyFleetError(err)) return EMPTY_DEVICES_LIST;
        throw err;
      }
    },

    async getSuccessRates(params) {
      const query = new URLSearchParams({
        deviceOffset: String(params.deviceOffset),
        deviceLimit: String(params.deviceLimit),
        jobOffset: String(params.jobOffset),
        jobLimit: String(params.jobLimit),
        sortBy: params.sortBy,
        sortDirection: params.sortDirection,
      });
      const dto = await client.get<SuccessRatesResponseDto>(
        `/api/v1/device/automation-success-rates?${query.toString()}`,
      );
      return mapSuccessRates(dto);
    },

    async getLoadStats(period) {
      const dto = await client.get<LoadStatsResponseDto>(
        `/api/v1/device/load-stats?period=${period}`,
      );
      return mapLoadStats(dto);
    },

    async deleteDevice(deviceId) {
      await client.del("/api/v1/device/delete_device", {
        body: JSON.stringify({ deviceId }),
        headers: { "Content-Type": "application/json" },
      });
    },

    /**
     * List devices on the rental market. The backend validates the body in a
     * fixed order — prices, then uptime guarantee, then payment type, then the
     * devices themselves — and rejects anything incomplete with a 400. Sending
     * only `device_Ids` (what v5 did before the market form existed) always
     * failed with "rent price required", so every field goes on the wire here.
     */
    async publishToMarket({ deviceIds, paymentType, uptimeGuarantee, prices }) {
      await client.post("/api/v1/device/market/publish", {
        device_Ids: deviceIds,
        payment_type: paymentType,
        uptime_guarantee: uptimeGuarantee,
        rent_price_hourly: prices.hourly ?? null,
        rent_price_daily: prices.daily ?? null,
        rent_price_weekly: prices.weekly ?? null,
        rent_price_monthly: prices.monthly ?? null,
      });
    },

    async unpublishFromMarket(deviceIds) {
      await client.post("/api/v1/device/market/unpublish", {
        device_Ids: deviceIds,
      });
    },

    async addDevice(hashcode) {
      // This route reports a rejected code as **200** `{success:false,
      // message:"Invalid device code!"}`, not a 4xx — so `http.ts` sees a
      // success and throws nothing. Ignoring the body made every bad code look
      // like a completed pairing: the form closed and the list refetched.
      // Verified against the live backend 2026-08-14.
      const dto = await client.post<AddDeviceResponseDto>(
        "/api/v1/device/add_device",
        { hashcode },
      );
      if (dto?.success === false) {
        throw new ApiError(200, dto.message || "We couldn't add that device.", {
          body: dto,
        });
      }
    },

    async shareDevices({ deviceIds, username }) {
      // `device_Ids` (capital I) and `username` are both REQUIRED — v5 posted
      // `{device_ids}` with no username, so every Share click was a guaranteed
      // 400 with no error UI: a silent no-op, exactly like the original
      // Publish-to-Market defect. Probed against the live backend 2026-08-11:
      //   {}                     → 400 Required at "device_Ids"; Required at "username"
      //   {device_Ids, username} → accepted
      await client.post("/api/v1/device/share", {
        device_Ids: deviceIds,
        username,
      });
    },

    async searchMarket(params) {
      const query = new URLSearchParams({
        search: params.search,
        page: String(params.page),
        limit: String(params.limit),
      });
      try {
        const dto = await client.get<MarketResponseDto>(
          `/api/v1/device/market?${query.toString()}`,
        );
        return mapMarket(dto, params.page, params.limit);
      } catch (error) {
        // A search matching nothing is answered the same way an empty fleet is
        // — `404 {message:"No devices to display"}` — so the marketplace showed
        // "We couldn't load the marketplace… Retry" for the ordinary case of a
        // search with no hits. Same envelope, same treatment as `listDevices`.
        if (isEmptyFleetError(error)) {
          return { devices: [], total: 0, page: params.page, limit: params.limit };
        }
        throw error;
      }
    },

    async rentDevice(input) {
      await client.post("/api/v1/device/rent", {
        device_id: input.deviceId,
        rent_duration: input.duration,
      });
    },

    async getSettings(deviceId) {
      const dto = await client.post<DeviceSettingsDto>(
        "/api/v1/device/settings",
        { device_id: deviceId },
      );
      return mapDeviceSettings(dto);
    },

    async generateTriggerLink(deviceId, label) {
      await client.post("/api/v1/device/airplane-trigger/generate", {
        device_id: deviceId,
        label,
      });
    },

    async deleteTriggerLink(deviceId, triggerLinkId) {
      await client.post("/api/v1/device/airplane-trigger/delete", {
        device_id: deviceId,
        trigger_link_id: triggerLinkId,
      });
    },

    async setMinInterval(deviceId, interval) {
      await client.post("/api/v1/device/airplane-trigger/interval", {
        device_id: deviceId,
        interval,
      });
    },

    async setAutoTrigger(deviceId, interval) {
      await client.post("/api/v1/device/airplane-trigger/auto-trigger", {
        device_id: deviceId,
        interval,
      });
    },

    async getTriggerLogs(deviceId, skip, limit) {
      const dto = await client.post<TriggerLogsResponseDto>(
        "/api/v1/device/airplane-trigger/logs",
        { device_id: deviceId, skip, limit },
      );
      return mapTriggerLogs(dto);
    },

    // --- Buckets -----------------------------------------------------------
    async getBucket(remoteDeviceId) {
      const dto = await client.get<DeviceBucketResponseDto>(
        `/api/v1/device-bucket/${encodeURIComponent(remoteDeviceId)}`,
      );
      return mapDeviceBucket(dto);
    },

    async saveBucket(remoteDeviceId, data) {
      const dto = await client.put<DeviceBucketResponseDto>(
        `/api/v1/device-bucket/${encodeURIComponent(remoteDeviceId)}`,
        { data },
      );
      return mapDeviceBucket(dto);
    },

    async deleteBucket(remoteDeviceId) {
      await client.del(
        `/api/v1/device-bucket/${encodeURIComponent(remoteDeviceId)}`,
      );
    },

    // --- Proxy -------------------------------------------------------------
    async listProxyConnections() {
      // Connections + a best-effort bandwidth roll-up (bandwidth failures must
      // not blank out the whole tab — the legacy tab treated it as optional).
      const [list, bandwidth] = await Promise.all([
        client.post<ProxyConnectionsResponseDto>(
          "/api/v1/device/connections/list",
          {},
        ),
        client
          .post<ProxyBandwidthResponseDto>("/api/v1/device/proxy-bandwidth", {})
          .catch(() => ({ rows: [] }) as ProxyBandwidthResponseDto),
      ]);
      const byConnection = aggregateBandwidth(bandwidth);
      return (list.connections ?? []).map((c) =>
        mapProxyConnection(c, byConnection[c.connectionId ?? ""]),
      );
    },

    async createProxyConnection(deviceId, name) {
      const body: { device_id: string; name?: string } = { device_id: deviceId };
      if (name && name.trim()) body.name = name.trim();
      const dto = await client.post<ProxyConnectionResponseDto>(
        "/api/v1/device/connections/create",
        body,
      );
      return mapProxyConnection(dto.connection ?? {});
    },

    async deleteProxyConnection(connectionId) {
      await client.post("/api/v1/device/connections/delete", {
        connection_id: connectionId,
      });
    },

    async setProxyAssociation(connectionId, deviceId) {
      await client.post("/api/v1/device/connections/association/set", {
        connection_id: connectionId,
        device_id: deviceId,
      });
    },

    async rotateProxyPassword(connectionId) {
      const dto = await client.post<ProxyRotateResponseDto>(
        "/api/v1/device/connections/password/rotate",
        { connection_id: connectionId },
      );
      return dto.password ?? "";
    },

    async listProxyBlacklist(connectionId) {
      const dto = await client.post<ProxyBlacklistResponseDto>(
        "/api/v1/device/connections/blacklist/list",
        { connection_id: connectionId },
      );
      return dto.domains ?? [];
    },

    async addProxyBlacklist(connectionId, domains) {
      await client.post("/api/v1/device/connections/blacklist/add", {
        connection_id: connectionId,
        domains,
      });
    },

    async removeProxyBlacklist(connectionId, domains) {
      await client.post("/api/v1/device/connections/blacklist/remove", {
        connection_id: connectionId,
        domains,
      });
    },

    // --- Notifications -----------------------------------------------------
    async listDeviceNotifications(page, limit) {
      const dto = await client.get<DeviceNotificationsResponseDto>(
        `/api/v1/notification/notifications?page=${page}&limit=${limit}`,
      );
      return mapDeviceNotifications(dto);
    },

    async markNotificationsRead(notificationIds) {
      const dto = await client.post<ReadNotificationsResponseDto>(
        "/api/v1/notification/read_notifications",
        { notification_ids: notificationIds },
      );
      return Math.max(0, Math.trunc(Number(dto.unread ?? 0)));
    },

    // --- Automations -------------------------------------------------------
    async launchAutomation(input) {
      const body: Record<string, unknown> = {
        device_ids: input.deviceIds,
        automationId: input.automationId,
        command: input.command,
      };
      if (input.command === "start") {
        if (input.automationParameters)
          body.automationParameters = input.automationParameters;
        if (input.jobVariables) body.jobVariables = input.jobVariables;
      }
      const dto = await client.post<LaunchAutomationResponseDto>(
        "/api/v1/device/automation",
        body,
      );
      return mapLaunchAutomation(dto);
    },
  };
}

/** Default repository wired to the app's singleton HTTP client. */
export const devicesRepository = createDevicesRepository(http);
